import { Component, Input, OnChanges, OnDestroy, SimpleChanges } from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { createPlotlyChart } from 'src/app/_shared/plotlyConfig';
import { FleetChargingFigureService } from 'src/app/_services/fleet-charging-figure.service';
import { RbUiComponentsModule } from '@inst-iot/bosch-angular-ui-components'; 
import { TransformXAxisPipe } from 'src/app/_shared/pipes/transform-xaxis.pipe';

@Component({
  selector: 'app-charged-energy-ef-chart',
  standalone: true,
  imports: [CommonModule,RbUiComponentsModule],
  templateUrl: './charged-energy-ef-chart.component.html',
  styleUrls: ['./charged-energy-ef-chart.component.scss']
})
export class ChargedEnergyEfChartComponent implements OnChanges, OnDestroy {
  private readonly unsubscribe$ = new Subject<void>();
  @Input() selectedVehicles: any[] = [];
  @Input() vehiclesChargingInsightsDataPoint: any = {};
  @Input() fleetId: string[] = [];
  errorMessage: boolean = false;
  dataLoading: boolean = false;
  private colorMap: {[key:string]:string}={};
  chartRange!: { range: [number, number] };

  constructor(
    private readonly fleetChargingFigureService: FleetChargingFigureService,
  ) { }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.dataLoading = true;
    this.errorMessage = false;
    this.fleetChargingFigureService.getChargedEnergyFleetEF(this.fleetId).pipe(takeUntil(this.unsubscribe$)).subscribe(
      {
        next: (datapoints) => {
          const fleetData = this.plotFleetData(datapoints);
          this.fetchVehicleInfoAndUpdateChart(fleetData);
        },
        error: (error) => {
          console.error('Error fetching Charged Energy Accumulated EF data:', error);
          this.dataLoading = false;
          this.errorMessage = true;
        }
      }
    )
  }

  fetchVehicleInfoAndUpdateChart(fleetData: any[]): void {
    const deviceSerialNumber: string[] = this.selectedVehicles.map(vehicle => vehicle.batteryIdentifier);
    const vehicleNameMap: { [key: string]: string } = {};

    this.selectedVehicles.forEach(vehicle => {
      vehicleNameMap[vehicle.batteryIdentifier] = vehicle.name;
    });

    const averageLines = deviceSerialNumber.flatMap((id, index) => {
      const chargedEnergy = this.vehiclesChargingInsightsDataPoint?.[id]?.chargedEnergy;
      if (chargedEnergy == null) {
        return [];
      }

      const vehicleName = vehicleNameMap[id];

      const maxPercentAmount = Math.max(...fleetData.flatMap(plot => plot.y));

      return [{
        x: [chargedEnergy, chargedEnergy],
        y: [0, maxPercentAmount],
        type: 'scatter',
        mode: 'lines',
        name: vehicleName,
        line: {
          color: this.getVehicleColor(index),
          width: 4 
        }
      }];
    });

    const combinedPlotData = [...fleetData, ...averageLines];
    this.updateChart(combinedPlotData);
    this.dataLoading = false;
  }

  getVehicleColor(index: number): string {
    const colors = ['#007BC0', '#18837E','#9E2896'];
    return colors[index % 3];
  }

  plotFleetData(datapoints: any): any [] {
    const transformXAxisPipe = new TransformXAxisPipe();
    return Object.keys(datapoints).flatMap((fleetId: string) => {
      const plotData = datapoints[fleetId];
      const socDelta = plotData.map((dataObject: any) => dataObject.binEnergy);
      const chargingEvents = plotData.map((dataObject: any) => dataObject.percVeh);
      this.chartRange = transformXAxisPipe.transform(socDelta, chargingEvents, 10, 100);
      // Convert bin values to a more readable range - TODO: remove this logic
      // const factor = 100000;
      // const socDeltaReadable = socDelta.map((bin: any) => bin / factor); 
    
      return {
        x: socDelta,
        y: chargingEvents,
        type: 'bar',
        name: `Fleet`,
        marker: {
          color: '#8A9097' 
        }
      };
    });
  }

  updateChart(data: any[]): void {
    const layout = {
      title: 'Charged Energy (accumulated)',
      font: {
        family: 'boschsans, Helvetica Neue, Helvetica, Arial, sans-serif',
        size: 13,
      },
      barmode: 'group',
      xaxis: {
        title: 'Charged energy [Wh]',
        range: this.chartRange?.range
      },
      yaxis: {
        title: 'Relative number of fleet vehicles [%]'
      },
      legend: {
        orientation: 'h',
        y: -0.3,
        xanchor: 'center',
        x: 0.5,
        font: {
          family: 'boschsans, Helvetica Neue, Helvetica, Arial, sans-serif',
          size: 13,
        },
      }
    };

    createPlotlyChart('charged-energy-ef-chart', data, layout);
  }

  initializeColorMap(): void {
    const colors = ['#007BC0', '#18837E','#9E2896'];
    this.selectedVehicles.forEach((vehicle, index) => {
      this.colorMap[vehicle.batteryIdentifier] = colors[index % colors.length];
    });
  }

}
